// Loads/unloads third-party analytics scripts based on cookie consent.
// Nothing here runs until CookieConsentContext calls enableAnalytics(), so
// no analytics cookies are set before the visitor opts in.
import { GA_MEASUREMENT_ID, initAnalytics, disableAnalytics as disableFirebaseAnalytics } from './firebaseAnalytics';

let gtagScriptLoaded = false;

function loadGtagScript() {
  if (gtagScriptLoaded || typeof window === 'undefined') {
    return;
  }
  gtagScriptLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** Start Google Analytics (gtag) and Firebase Analytics collection. */
export function enableAnalytics() {
  if (typeof window === 'undefined') {
    return;
  }
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  loadGtagScript();
  initAnalytics();
}

// window['ga-disable-<id>'] only stops *future* hits - it doesn't remove
// cookies GA already set while the visitor was opted in. If they withdraw
// consent later, those cookies would otherwise just sit there unused until
// they expire naturally (up to 2 years for `_ga`), which isn't a genuine
// withdrawal of consent. Clear every _ga*/_gid/_gat cookie on both the
// current host and the parent domain (GA sets these domain-wide, e.g.
// `.naijanestproperties.com`, not just on the subdomain that set them).
function clearAnalyticsCookies() {
  if (typeof document === 'undefined') {
    return;
  }
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => /^_ga|^_gid$|^_gat/.test(name));

  const hostParts = window.location.hostname.split('.');
  const domains = [window.location.hostname];
  if (hostParts.length > 1) {
    domains.push(`.${hostParts.slice(-2).join('.')}`);
  }

  names.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    });
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
}

/** Stop Google Analytics and Firebase Analytics collection immediately, and
 *  remove any cookies already set while the visitor was opted in. */
export function disableAnalytics() {
  if (typeof window === 'undefined') {
    return;
  }
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  disableFirebaseAnalytics();
  clearAnalyticsCookies();
}

/**
 * Send a GA4 conversion-relevant event (e.g. view_property,
 * contact_agent_click, favorite_added, signup_completed). Silently no-ops
 * if gtag hasn't loaded yet - i.e. before the visitor has consented to the
 * Analytics cookie category - so this never sends anything without consent
 * and never throws for callers that fire it unconditionally.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', name, params);
}
